import React, { useState, useEffect, useRef } from 'react';
import Header from '../components/header';
import DefaultFooter from '../components/default-footer';

// ============================================================
// WorldwideHotels.tsx
// 
// 📸 VOICI LES PHOTOS UTILISÉES :
// - Hero: https://images.unsplash.com/photo-1566073771259-6a8506099945
// - Dubai (Burj Al Arab): https://images.unsplash.com/photo-1512453979798-5ea266f8880c
// - Abu Dhabi (Emirates Palace): https://images.unsplash.com/photo-1718873003348-be42d37ac198
// - Oman (The Chedi): https://images.unsplash.com/photo-1682695795255-b236b1f1267d
// - Morocco (Royal Mansour): https://images.unsplash.com/photo-1674813800347-410fbe365ddf
// - Egypt (Four Seasons): https://images.unsplash.com/photo-1538316622620-9d54a7bd01ef
// - Saudi (St. Regis): https://images.unsplash.com/photo-1699023654953-ce7fd12741fb
// ============================================================

function DuneRule() {
  return (
    <div aria-hidden="true" style={{ lineHeight: 0 }}>
      <svg viewBox="0 0 1200 34" width="100%" height="22" preserveAspectRatio="none">
        <path d="M0 24 C 90 4, 170 4, 250 20 S 400 30, 480 14 S 620 2, 700 18 S 850 30, 940 12 S 1090 2, 1200 16" fill="none" stroke="var(--gold-d)" strokeOpacity="0.55" strokeWidth="1.5" />
        <path d="M0 28 C 90 12, 170 12, 250 26 S 400 34, 480 20 S 620 10, 700 24 S 850 34, 940 18 S 1090 10, 1200 22" fill="none" stroke="rgba(41,75,90,.14)" strokeWidth="1" />
      </svg>
    </div>
  );
}

// ============================================================
// ICÔNES SVG
// ============================================================

function IconGlobe() {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--gold-d)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3c2.5 2.5 2.5 15.5 0 18" />
      <path d="M12 3c-2.5 2.5-2.5 15.5 0 18" />
    </svg>
  );
}

function IconStar() {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--gold-d)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 3 14.7 9.3 21.2 9.3 16.3 13.5 18.5 19.7 12 16.5 5.5 19.7 7.7 13.5 2.8 9.3 9.3 9.3 12 3" />
    </svg>
  );
}

function IconShield() {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--gold-d)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}

function IconBuilding() {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--gold-d)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="2" width="16" height="20" rx="1" />
      <line x1="8" y1="6" x2="16" y2="6" />
      <line x1="8" y1="10" x2="16" y2="10" />
      <line x1="8" y1="14" x2="16" y2="14" />
      <line x1="8" y1="18" x2="12" y2="18" />
    </svg>
  );
}

// ============================================================
// 📸 TOUTES LES PHOTOS AVEC LEURS URLS
// ============================================================

const IMAGES = {
  // Hero - Hôtel de luxe
  hero: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1800&q=80&fit=crop&crop=center',
  
  // Dubai - Burj Al Arab
  dubai: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=1200&q=80&fit=crop&crop=center',
  
  // Abu Dhabi - Emirates Palace
  abuDhabi: 'https://images.unsplash.com/photo-1718873003348-be42d37ac198?w=1200&q=80&fit=crop&crop=center',
  
  // Oman - The Chedi Muscat
  oman: 'https://images.unsplash.com/photo-1682695795255-b236b1f1267d?w=1200&q=80&fit=crop&crop=center',
  
  // Morocco - Royal Mansour Marrakech (riad courtyard, Marrakech medina)
  morocco: 'https://images.unsplash.com/photo-1674813800347-410fbe365ddf?w=1200&q=80&fit=crop&crop=center',
  
  // Egypt - Four Seasons Nile Plaza (Nile river, Cairo)
  egypt: 'https://images.unsplash.com/photo-1538316622620-9d54a7bd01ef?w=1200&q=80&fit=crop&crop=center',
  
  // Saudi Arabia - St. Regis Riyadh (Riyadh skyline, Faisaliah Tower)
  saudi: 'https://images.unsplash.com/photo-1699023654953-ce7fd12741fb?w=1200&q=80&fit=crop&crop=center',
};

// ============================================================
// DONNÉES
// ============================================================

interface Highlight {
  icon: React.ReactNode;
  title: string;
  description: string;
}

interface Destination {
  id: string;
  name: string;
  hotel: string;
  description: string;
  image: string;
  features: string[];
}

interface Step {
  number: string;
  title: string;
  description: string;
}

interface Faq {
  question: string;
  answer: string;
}

const highlights: Highlight[] = [
  {
    icon: <IconGlobe />,
    title: "MENA Region Experts",
    description: "Deep knowledge of luxury properties across Dubai, Abu Dhabi, Oman, Saudi Arabia, Morocco, and Egypt.",
  },
  {
    icon: <IconStar />,
    title: "Preferred Partner Rates",
    description: "Access to negotiated rates and exclusive perks at the region's finest hotels and resorts.",
  },
  {
    icon: <IconShield />,
    title: "Room & View Guarantees",
    description: "Sea views, higher floors, connecting rooms — we ensure your preferences are confirmed.",
  },
  {
    icon: <IconBuilding />,
    title: "Curated Portfolio",
    description: "Every property is personally vetted — we only recommend hotels we would stay in ourselves.",
  },
];

const destinations: Destination[] = [
  {
    id: 'dubai',
    name: 'Dubai',
    hotel: 'Burj Al Arab Jumeirah',
    description: 'The world\'s most iconic hotel — a sail-shaped marvel offering unparalleled luxury, private beaches, and breathtaking views of the Arabian Gulf.',
    image: IMAGES.dubai,
    features: ['Private beach', 'Butler service', 'Helipad', '9 restaurants'],
  },
  {
    id: 'abu-dhabi',
    name: 'Abu Dhabi',
    hotel: 'Emirates Palace',
    description: 'A palatial beachfront resort that redefines Arabian luxury — with private marina, gold-plated interiors, and world-class hospitality.',
    image: IMAGES.abuDhabi,
    features: ['Private marina', 'Gold-plated interiors', 'Spa', 'Michelin-star dining'],
  },
  {
    id: 'oman',
    name: 'Oman',
    hotel: 'The Chedi Muscat',
    description: 'A serene beachfront sanctuary blending Omani architecture with contemporary luxury — set against the dramatic Hajar Mountains.',
    image: IMAGES.oman,
    features: ['Beachfront', 'Mountain views', 'Spa', 'Authentic Omani cuisine'],
  },
  {
    id: 'morocco',
    name: 'Morocco',
    hotel: 'Royal Mansour Marrakech',
    description: 'A palatial retreat in the heart of the Medina — where Moroccan craftsmanship meets royal hospitality.',
    image: IMAGES.morocco,
    features: ['Palace setting', 'Private riads', 'Spa', 'Gardens'],
  },
  {
    id: 'egypt',
    name: 'Egypt',
    hotel: 'Four Seasons Nile Plaza',
    description: 'Overlooking the timeless Nile River — luxury accommodation with views of Cairo\'s iconic skyline and pyramids beyond.',
    image: IMAGES.egypt,
    features: ['Nile views', 'Spa', 'Pool', 'Fine dining'],
  },
  {
    id: 'saudi',
    name: 'Saudi Arabia',
    hotel: 'St. Regis Riyadh',
    description: 'Sophisticated urban elegance in the heart of the Kingdom — a blend of traditional hospitality and modern luxury.',
    image: IMAGES.saudi,
    features: ['Butler service', 'Spa', 'Business facilities', 'Fine dining'],
  },
];

const steps: Step[] = [
  {
    number: '01',
    title: 'Tell Us Your Vision',
    description: 'Destination, dates, budget, and the experience you\'re looking for — beachfront, city, or desert retreat.',
  },
  {
    number: '02',
    title: 'We Shortlist the Best',
    description: 'A curated selection of 2–3 exceptional properties with clear reasoning behind each recommendation.',
  },
  {
    number: '03',
    title: 'Request & Confirm',
    description: 'Room preferences, view requests, and loyalty numbers are all communicated directly to the hotel.',
  },
  {
    number: '04',
    title: 'Ongoing Support',
    description: 'Date changes, special requests, or any adjustments — we handle it all on your behalf.',
  },
];

const faqs: Faq[] = [
  {
    question: 'Which hotels do you recommend in Dubai?',
    answer: 'We work with the finest properties including Burj Al Arab, Atlantis The Royal, and the Four Seasons DIFC. Each is carefully matched to your preferences.',
  },
  {
    question: 'Do you have access to exclusive rates?',
    answer: 'Yes, through our preferred partnerships with leading hotel groups, we often secure better rates and added perks like upgrades and late check-outs.',
  },
  {
    question: 'Can you arrange room blocks for events?',
    answer: 'Absolutely. For weddings, corporate events, or family gatherings, we manage room blocks and negotiate group rates at premier properties.',
  },
  {
    question: 'What about last-minute bookings?',
    answer: 'We specialize in securing the best available options even on short notice, especially for our preferred partner properties.',
  },
];

// ============================================================
// COMPOSANT
// ============================================================

export default function WorldwideHotels() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const statsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.3 }
    );

    const statItems = document.querySelectorAll('.stat-item');
    statItems.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap');

        :root {
          --teal: #294b5a;
          --teal-d: #1a3140;
          --teal-l: #3b6272;
          --gold: #d3be76;
          --gold-l: #ede4b5;
          --gold-d: #b09a50;
          --cream: #f7f4ee;
          --mist: #edeae2;
          --silver: #8a9aa3;
          --ink: #1a2f38;
          --radius: 10px;
          --shadow-sm: 0 6px 20px rgba(26, 49, 61, .07);
          --shadow-md: 0 20px 48px rgba(26, 49, 61, .12);
          --shadow-lg: 0 28px 64px rgba(26, 49, 61, .18);
        }

        * { box-sizing: border-box; }
        .visa-page { font-family: 'DM Sans', sans-serif; background: var(--cream); -webkit-font-smoothing: antialiased; }
        .visa-page h1, .visa-page h2, .visa-page h3 { font-family: 'Playfair Display', serif; }

        .eyebrow { display: inline-flex; align-items: center; gap: 10px; font-size: 11px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; color: var(--gold-d); }
        .eyebrow::before { content: ''; width: 22px; height: 2px; background: var(--gold); border-radius: 2px; }
        .eyebrow.light { color: var(--gold-l); }
        .eyebrow.light::before { background: var(--gold-l); opacity: .6; }
        .eyebrow.centered { justify-content: center; }

        .section-title { font-size: clamp(1.7rem, 2.6vw, 2.4rem); font-weight: 500; color: var(--ink); line-height: 1.2; margin: 10px 0 12px; }
        .section-title.centered { text-align: center; }
        .section-sub { font-size: 15px; color: var(--silver); font-weight: 300; line-height: 1.7; max-width: 520px; }
        .section-sub.centered { margin: 0 auto; text-align: center; }
        .section-pad { padding: 84px 0; }
        @media (max-width: 991px) { .section-pad { padding: 64px 0; } }
        @media (max-width: 576px) { .section-pad { padding: 48px 0; } }
        .bg-cream { background: var(--cream); }
        .bg-white { background: #fff; }
        .bg-teal { background: var(--teal-d); }

        .vbtn-primary { display: inline-flex; align-items: center; gap: 8px; background: var(--gold); color: var(--teal-d); border-radius: 5px; font-size: 12.5px; font-weight: 700; letter-spacing: .3px; padding: 14px 26px; text-decoration: none; transition: transform .2s, background .2s; box-shadow: 0 10px 28px rgba(211,190,118,.28); border: none; cursor: pointer; }
        .vbtn-primary:hover { background: #e2ce86; transform: translateY(-2px); }
        .vbtn-ghost { display: inline-flex; align-items: center; background: rgba(255,255,255,.1); backdrop-filter: blur(4px); color: #fff; border: 1px solid rgba(255,255,255,.25); border-radius: 5px; font-size: 12.5px; font-weight: 600; letter-spacing: .3px; padding: 14px 24px; text-decoration: none; transition: background .2s, border-color .2s; }
        .vbtn-ghost:hover { border-color: #fff; background: rgba(255,255,255,.18); }

        /* HERO */
        .svc-hero { position: relative; min-height: 500px; display: flex; align-items: center; overflow: hidden; background: var(--teal-d); }
        .svc-hero-img { position: absolute; inset: 0; background-size: cover; background-position: center; opacity: 0.28; }
        .svc-hero-veil { position: absolute; inset: 0; background: linear-gradient(180deg, rgba(26,49,61,.55) 0%, rgba(26,49,61,.92) 100%); }
        .svc-hero-inner { position: relative; z-index: 2; padding: 64px 0 40px; }
        .svc-hero-title { font-weight: 600; color: #fff; line-height: 1.16; font-size: clamp(30px, 4vw, 48px); max-width: 660px; margin-bottom: 16px; }
        .svc-hero-title .gold { font-style: italic; color: var(--gold); font-weight: 400; }
        .svc-hero-sub { font-size: 15px; color: rgba(255,255,255,.78); font-weight: 300; line-height: 1.75; max-width: 540px; margin-bottom: 30px; }
        .svc-hero-ctas { display: flex; gap: 12px; flex-wrap: wrap; }
        .svc-breadcrumb { font-size: 11.5px; color: rgba(255,255,255,.55); margin-bottom: 18px; }
        .svc-breadcrumb a { color: rgba(255,255,255,.75); text-decoration: none; }
        .svc-breadcrumb a:hover { color: var(--gold-l); }

        /* STATS */
        .stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 30px; }
        @media (max-width: 768px) { .stats-grid { grid-template-columns: 1fr 1fr; } }
        @media (max-width: 480px) { .stats-grid { grid-template-columns: 1fr; } }
        .stat-item {
          text-align: center;
          padding: 20px;
          opacity: 0;
          transform: translateY(20px);
          transition: opacity .8s ease, transform .8s ease;
        }
        .stat-item.visible { opacity: 1; transform: translateY(0); }
        .stat-item .value { font-family: 'Playfair Display', serif; font-size: 2.6rem; font-weight: 600; color: var(--gold); line-height: 1.1; }
        .stat-item .label { font-size: 13px; color: rgba(255,255,255,.7); font-weight: 300; margin-top: 4px; }

        /* HIGHLIGHTS */
        .highlight-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 28px; }
        @media (max-width: 900px) { .highlight-grid { grid-template-columns: 1fr 1fr; } }
        @media (max-width: 560px) { .highlight-grid { grid-template-columns: 1fr; } }
        .highlight-cell {
          background: #fff;
          border-radius: var(--radius);
          padding: 28px 24px;
          border: 1px solid rgba(41,75,90,.06);
          text-align: center;
          transition: border-color .3s ease, box-shadow .3s ease, transform .3s ease;
        }
        .highlight-cell:hover { border-color: rgba(211,190,118,.5); box-shadow: var(--shadow-sm); transform: translateY(-3px); }
        .highlight-cell .hl-icon { display: flex; align-items: center; justify-content: center; margin-bottom: 14px; color: var(--gold-d); }
        .highlight-cell h3 { font-size: 14.5px; font-weight: 600; color: var(--ink); margin-bottom: 6px; }
        .highlight-cell p { font-size: 12.5px; color: var(--silver); font-weight: 300; line-height: 1.65; margin: 0; }

        /* DESTINATIONS */
        .dest-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 28px; }
        @media (max-width: 992px) { .dest-grid { grid-template-columns: 1fr 1fr; } }
        @media (max-width: 640px) { .dest-grid { grid-template-columns: 1fr; } }

        .dest-card {
          background: #fff;
          border-radius: var(--radius);
          overflow: hidden;
          box-shadow: var(--shadow-sm);
          border: 1px solid rgba(41,75,90,.06);
          transition: transform .4s cubic-bezier(.22,1,.36,1), box-shadow .4s cubic-bezier(.22,1,.36,1);
        }
        .dest-card:hover { transform: translateY(-6px); box-shadow: var(--shadow-md); }
        .dest-card-img { 
          height: 220px; 
          background-size: cover; 
          background-position: center; 
          position: relative;
        }
        .dest-card-img .hotel-badge {
          position: absolute; bottom: 14px; left: 14px; right: 14px;
          background: rgba(26,49,61,.85); backdrop-filter: blur(8px);
          padding: 6px 16px; border-radius: 20px;
          color: var(--gold-l); font-size: 11px; font-weight: 500; letter-spacing: .3px;
          border: 1px solid rgba(255,255,255,.1);
          text-align: center;
        }
        .dest-card-body { padding: 22px 24px 26px; }
        .dest-card-body h3 { font-size: 1.15rem; font-weight: 600; color: var(--ink); margin-bottom: 2px; }
        .dest-card-body .hotel-name { font-size: 12px; color: var(--gold-d); font-weight: 500; display: block; margin-bottom: 6px; }
        .dest-card-body p { font-size: 13px; color: var(--silver); font-weight: 300; line-height: 1.6; margin-bottom: 12px; }
        .dest-features { display: flex; flex-wrap: wrap; gap: 4px 10px; }
        .dest-features span { font-size: 11px; color: var(--teal); font-weight: 400; }
        .dest-features span::before { content: '—'; margin-right: 6px; color: var(--gold); }

        /* PROCESS */
        .process-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 28px; }
        @media (max-width: 900px) { .process-grid { grid-template-columns: 1fr 1fr; } }
        @media (max-width: 560px) { .process-grid { grid-template-columns: 1fr; } }
        .process-step {
          background: #fff;
          border-radius: var(--radius);
          padding: 28px 24px;
          border: 1px solid rgba(41,75,90,.06);
          transition: border-color .3s ease, box-shadow .3s ease, transform .3s ease;
          text-align: center;
        }
        .process-step:hover { border-color: rgba(211,190,118,.5); box-shadow: var(--shadow-sm); transform: translateY(-3px); }
        .process-step .num { font-family: 'Playfair Display', serif; font-size: 2rem; font-weight: 600; color: var(--gold); opacity: .3; display: block; margin-bottom: 8px; }
        .process-step h3 { font-size: 15px; font-weight: 600; color: var(--ink); margin-bottom: 4px; }
        .process-step p { font-size: 13px; color: var(--silver); font-weight: 300; line-height: 1.7; margin: 0; }

        /* FAQ */
        .faq-list { max-width: 760px; margin: 0 auto; }
        .faq-item { border-bottom: 1px solid rgba(41,75,90,.1); }
        .faq-item:first-child { border-top: 1px solid rgba(41,75,90,.1); }
        .faq-q {
          width: 100%; background: none; border: none; text-align: left; cursor: pointer;
          display: flex; align-items: center; justify-content: space-between; gap: 20px;
          padding: 20px 4px; font-family: 'DM Sans', sans-serif; font-size: 14px;
          font-weight: 500; color: var(--ink);
        }
        .faq-q .plus { flex: none; width: 22px; height: 22px; border-radius: 50%; border: 1.5px solid var(--gold-d); position: relative; transition: transform .3s ease, background .3s ease; }
        .faq-q .plus::before, .faq-q .plus::after { content: ''; position: absolute; background: var(--gold-d); top: 50%; left: 50%; transform: translate(-50%,-50%); }
        .faq-q .plus::before { width: 9px; height: 1.4px; }
        .faq-q .plus::after { width: 1.4px; height: 9px; transition: opacity .2s ease; }
        .faq-item.open .plus { background: var(--gold-d); transform: rotate(180deg); }
        .faq-item.open .plus::before, .faq-item.open .plus::after { background: #fff; }
        .faq-item.open .plus::after { opacity: 0; }
        .faq-a { max-height: 0; overflow: hidden; transition: max-height .35s ease; }
        .faq-item.open .faq-a { max-height: 240px; }
        .faq-a p { font-size: 13.5px; color: var(--silver); font-weight: 300; line-height: 1.75; padding: 0 4px 24px; max-width: 640px; }

        /* CTA BANNER */
        .cta-banner { background: var(--teal); border-radius: var(--radius); padding: 44px 40px; display: flex; align-items: center; justify-content: space-between; gap: 30px; flex-wrap: wrap; }
        .cta-banner-title { color: #fff; font-size: 1.3rem; font-weight: 500; margin-bottom: 4px; }
        .cta-banner-sub { color: rgba(255,255,255,.7); font-size: 13px; font-weight: 300; max-width: 460px; }

        @media (max-width: 576px) {
          .cta-banner { padding: 28px 20px; }
          .dest-card-body { padding: 18px 18px 22px; }
          .process-step { padding: 22px 18px; }
          .highlight-cell { padding: 22px 18px; }
          .dest-card-img { height: 180px; }
        }
      `}</style>

      <div className="visa-page">
        <Header />

        {/* ===== HERO ===== */}
        <section className="svc-hero">
          <div className="svc-hero-img" style={{ backgroundImage: `url(${IMAGES.hero})` }} />
          <div className="svc-hero-veil" />
          <div className="container svc-hero-inner">
            <div className="svc-breadcrumb">
              <a href="/services">Services</a> / Worldwide Hotels
            </div>
            <div className="eyebrow light" style={{ marginBottom: 14 }}>
              Luxury Stays Across the Region
            </div>
            <h1 className="svc-hero-title">
              Worldwide Hotels, <span className="gold">Curated for You</span>
            </h1>
            <p className="svc-hero-sub">
              From the iconic Burj Al Arab to the serene Chedi Muscat — we shortlist the finest
              properties across the MENA region and beyond, matched to your preferences.
            </p>
            <div className="svc-hero-ctas">
              <a href="#destinations" className="vbtn-primary">Explore Hotels</a>
              <a href="/contact" className="vbtn-ghost">Get a Quote</a>
            </div>
          </div>
        </section>

        <DuneRule />

        {/* ===== HIGHLIGHTS ===== */}
        <section className="section-pad bg-white">
          <div className="container">
            <div style={{ marginBottom: 44 }}>
              <span className="eyebrow centered" style={{ justifyContent: 'center' }}>
                What Makes Us Different
              </span>
              <h2 className="section-title centered">Hotels Chosen With Care</h2>
            </div>
            <div className="highlight-grid">
              {highlights.map((h, idx) => (
                <div className="highlight-cell" key={idx}>
                  <div className="hl-icon">{h.icon}</div>
                  <h3>{h.title}</h3>
                  <p>{h.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <DuneRule />

        {/* ===== DESTINATIONS ===== */}
        <section className="section-pad bg-cream" id="destinations">
          <div className="container">
            <div style={{ marginBottom: 44 }}>
              <span className="eyebrow centered" style={{ justifyContent: 'center' }}>
                Our Portfolio
              </span>
              <h2 className="section-title centered">Exceptional Hotels Across the Region</h2>
              <p className="section-sub centered">
                From the Arabian Gulf to the Mediterranean — we curate the finest properties for
                your stay.
              </p>
            </div>
            <div className="dest-grid">
              {destinations.map((dest) => (
                <div className="dest-card" key={dest.id}>
                  <div className="dest-card-img" style={{ backgroundImage: `url(${dest.image})` }}>
                    <span className="hotel-badge">{dest.hotel}</span>
                  </div>
                  <div className="dest-card-body">
                    <h3>{dest.name}</h3>
                    <span className="hotel-name">{dest.hotel}</span>
                    <p>{dest.description}</p>
                    <div className="dest-features">
                      {dest.features.map((f, idx) => (
                        <span key={idx}>{f}</span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <DuneRule />

        {/* ===== PROCESS ===== */}
        <section className="section-pad bg-white">
          <div className="container">
            <div style={{ marginBottom: 44 }}>
              <span className="eyebrow centered" style={{ justifyContent: 'center' }}>
                How It Works
              </span>
              <h2 className="section-title centered">From Request to Reservation</h2>
            </div>
            <div className="process-grid">
              {steps.map((step) => (
                <div className="process-step" key={step.number}>
                  <span className="num">{step.number}</span>
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <DuneRule />

        {/* ===== FAQ ===== */}
        <section className="section-pad bg-cream">
          <div className="container">
            <div style={{ marginBottom: 36 }}>
              <span className="eyebrow centered" style={{ justifyContent: 'center' }}>
                Common Questions
              </span>
              <h2 className="section-title centered">Everything You Need to Know</h2>
            </div>
            <div className="faq-list">
              {faqs.map((f, idx) => {
                const isOpen = openFaq === idx;
                return (
                  <div className={`faq-item${isOpen ? ' open' : ''}`} key={f.question}>
                    <button
                      type="button"
                      className="faq-q"
                      onClick={() => setOpenFaq(isOpen ? null : idx)}
                      aria-expanded={isOpen}
                    >
                      <span>{f.question}</span>
                      <span className="plus" />
                    </button>
                    <div className="faq-a">
                      <p>{f.answer}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <DuneRule />

        {/* ===== CTA FINAL ===== */}
        <section className="section-pad bg-white" style={{ paddingTop: 0 }}>
          <div className="container">
            <div className="cta-banner">
              <div>
                <div className="cta-banner-title">Find Your Perfect Stay</div>
                <div className="cta-banner-sub">
                  Tell us your destination and preferences — we'll curate the finest properties
                  that match your vision.
                </div>
              </div>
              <a href="/contact" className="vbtn-primary">
                Get a Quote
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </a>
            </div>
          </div>
        </section>

        <DefaultFooter />
      </div>
    </>
  );
}